import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/axios'
import endpoints from '../../api/endpoints'
import { extractApiErrorMessage } from '../../utils/apiError'

export interface ProfileInfo {
  employee: {
    empNuid: string
    empFirstName: string
    empLastName: string
    empMiddleName: string
    approvalGrpId: string
    kpEmail: string
    alternateEmail: string
    kpSelected: boolean
    alternateSelected: boolean
    primaryApprover: string
    job: string
    union: string
    department: string
    location: string
    shift: string
    unionSeniorityDate: string
    alternateSeniorityDate: string
    regionCd: string
    unionId: string
    empType?: string | null
  }
  roles: {
    isAdmin: boolean
    isApprover: boolean
    isApproverProxy: boolean
    isRequestorProxy: boolean
    hasAuthorizedApprover: boolean
    approverGroupIds: string[]
    // Scoped admin flags: grant access to one specific Assign-Proxy feature
    // (and its landing page card) without full isAdmin privileges.
    isAPAdmin?: boolean
    isFEAdmin?: boolean
    isRPAdmin?: boolean
  }
  approvers: Array<{
    approverNuid: string
    approverFirstName: string
    approverLastName: string
    approverMiddleName: string
  }>
}

export interface ProfileState {
  data: ProfileInfo | null
  loading: boolean
  error: string | null
  initialized: boolean
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ProfileState = {
  data: null,
  loading: false,
  error: null,
  initialized: false,
}

const getEmployeeDisplayName = (employee: ProfileInfo['employee']): string =>
  [employee.empFirstName, employee.empMiddleName, employee.empLastName]
    .filter(Boolean)
    .join(' ')

const persistProfileActor = (profile: ProfileInfo): void => {
  if (typeof sessionStorage === 'undefined') return

  const userName = getEmployeeDisplayName(profile.employee)
  if (userName) sessionStorage.setItem('userName', userName)
  if (profile.employee.empNuid) sessionStorage.setItem('nuid', profile.employee.empNuid)
}

// ============================================================================
// ASYNC THUNK
// ============================================================================

export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (force: boolean | undefined, { rejectWithValue, signal, getState }) => {
    const shouldForce = force ?? false
    const state = getState() as { profile: ProfileState }
    if (!shouldForce && state.profile.data) {
      persistProfileActor(state.profile.data)
      return state.profile.data
    }
    try {
      const options: any = { signal }
      const response = await api.get(endpoints.profile, options)
      const profile = response.data as ProfileInfo
      persistProfileActor(profile)
      return profile
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch profile')
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Profile - Pending
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true
        state.error = null
        state.initialized = false
      })
      // Fetch Profile - Fulfilled
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
        state.initialized = true
      })
      // Fetch Profile - Rejected
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.initialized = true
      })
  },
})

export const profileData = (state: { profile: ProfileState }) =>
  state.profile.data

export default profileSlice.reducer
